import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  ArrowRight, 
  Clock, 
  Globe, 
  CheckCircle, 
  FileText,
  EnvelopeSimple,
  BookOpen,
  ChartLine,
  Calendar,
  Users,
  House,
  Newspaper,
  Archive,
  PencilLine,
  UsersFour,
  Printer,
  FilePdf,
  DownloadSimple,
  Eye,
  ArrowsOut,
  Copy,
  X
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import { DEFAULT_JOURNALS } from "@/lib/mockData";
import { JOURNAL_ARTICLES } from "@/lib/articlesData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// Extended journal data with detailed content
const journalDetails = {
  jcrr: {
    fullTitle: "Journal of Clinical Research and Reviews",
    shortTitle: "JCRR",
    tagline: "Peer-Reviewed Open Access Clinical & Medical Research",
    description: `Welcome to the Journal of Clinical Research and Reviews (JCRR) — an international, open-access journal giving special importance to publishing research and reviews in the fields of general & scientific medical research and clinical practice. It also publishes valuable studies in Cardiology, Neurology, Obstetrics and Gynaecology, Surgery, Internal medicine, Orthopaedics, Infectious diseases, and HIV/AIDS, Oncology and all related areas which come under the journal scope.

All the articles accepted for publication in the journal "Journal of Clinical Research and Reviews" are freely available online immediately upon publication.`,
    about: `Journal of Clinical Research and Reviews requests and encourages researchers to submit their brilliantly orchestrated topics or recent developments in the field of Clinical and Medical areas in the form of Research and Reviews.

Journal gives special importance to publish research and reviews in the fields of general & scientific medical research and clinical practice. The Journal aims to reflect contemporary thinking so that professionals can keep pace with developments in the field of Clinical-Medical.

JCRR welcomes direct submissions of manuscripts from authors. Manuscripts could be submitted through our online portal or as directed to the editorial office as an attachment to email at: info@iscienceqpublishers.com or isciencepublishers@gmail.com.`,
    scope: [
      { category: "Clinical & Specialty Medicine", topics: "Cardiology, Neurology, Obstetrics and Gynaecology, Surgery, Internal Medicine, Orthopaedics" },
      { category: "Infectious Diseases & Oncology", topics: "Infectious Diseases, HIV/AIDS, Oncology & All Related Clinical Disciplines" },
      { category: "General & Scientific Medical Research", topics: "General Medical Research, Scientific Medical Research, Clinical Practice & Contemporary Medical Thinking" },
      { category: "Submission Formats", topics: "Brilliantly Orchestrated Research Articles, Reviews & Recent Developments in Clinical and Medical Areas" }
    ],
    editorialBoard: [
      { name: "Dr. Sarah Mitchell", role: "Editor-in-Chief", institution: "Harvard Medical School, USA", specialty: "Clinical Research & Internal Medicine" },
      { name: "Prof. James Chen", role: "Associate Editor", institution: "Oxford University, UK", specialty: "Cardiology & Scientific Medical Practice" },
      { name: "Dr. Maria Rodriguez", role: "Senior Editor", institution: "Karolinska Institute, Sweden", specialty: "Oncology & Infectious Diseases" },
      { name: "Prof. Ahmed Hassan", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Surgery & Clinical Practice" }
    ]
  },
  ijccr: {
    fullTitle: "International Journal of Cardiology & Cardiovascular Research",
    shortTitle: "IJCCR",
    tagline: "Peer-Reviewed Open Access Cardiology Therapeutics & Vascular Sciences",
    description: `Welcome to the International Journal of Cardiology & Cardiovascular Research (IJCCR) — a peer-reviewed open-access online journal aimed at publishing seminal research that focuses primarily on cardiology therapeutics, proficiently supported by universally prominent Editorial Board members.

The journal aims to encourage researchers to publish their new findings or ideas in an open-access platform and there is no restriction on the length of the papers so that full experimental details provided by the author should help in better understanding by the readers.

All the articles accepted for publication in the journal "International Journal of Cardiology & Cardiovascular Research" are freely available online immediately upon publication.`,
    about: `The International Journal of Cardiology & Cardiovascular Research welcomes all types of papers across a wide range of topics in this discipline and creates a platform for authors to contribute towards advancement in Cardiovascular Medicine, Cardiac Surgeons, Cardiomyopathy, Cardiovascular Pharmacology, Neuro Cardiology, Cardiac Anesthesiology, Pediatric Cardiology, Disorders of Heart, Veterinary Cardiology, Medical Cardiologists, Vascular Medicine, Nuclear Cardiology, Cardiovascular Imaging, Interventional Cardiology, Heart Failure, Vascular Surgeons, Coronary Artery Disease, Cardiovascular Disorders, Cardiovascular Pathology, Electrophysiology Heart Diseases, Rheumatic Heart Disease, Coronary Disease, Cardiovascular Drugs, Cerebral Vascular Disease, Cardiology Clinics, Clinical Trials, Stroke and Acute Myocardial Infarction.

IJCCR welcomes direct submissions of manuscripts from authors. Manuscripts could be submitted through our online portal or as directed to the editorial office as an attachment to email at: info@iscienceqpublishers.com or isciencepublishers@gmail.com.`,
    scope: [
      { category: "Cardiology Therapeutics & Clinical Interventions", topics: "Cardiology Therapeutics, Interventional Cardiology, Heart Failure, Coronary Artery Disease, Acute Myocardial Infarction, Stroke, Cardiology Clinics & Clinical Trials" },
      { category: "Cardiac Surgery & Vascular Medicine", topics: "Cardiovascular Medicine, Cardiac Surgeons, Vascular Surgeons, Vascular Medicine, Coronary Disease, Disorders of Heart, Rheumatic Heart Disease & Cerebral Vascular Disease" },
      { category: "Cardiovascular Pharmacology & Pathology", topics: "Cardiovascular Pharmacology, Cardiovascular Drugs, Electrophysiology Heart Diseases, Cardiovascular Pathology, Cardiomyopathy & Neuro Cardiology" },
      { category: "Specialized Cardiology & Diagnostic Imaging", topics: "Pediatric Cardiology, Veterinary Cardiology, Cardiac Anesthesiology, Nuclear Cardiology & Cardiovascular Imaging" },
      { category: "Unrestricted Paper Length", topics: "No restriction on length of papers to ensure full experimental details are provided for complete understanding" }
    ],
    editorialBoard: [
      { name: "Prof. James Chen", role: "Editor-in-Chief", institution: "Oxford University, UK", specialty: "Cardiology & Vascular Medicine" },
      { name: "Dr. Sarah Mitchell", role: "Associate Editor", institution: "Harvard Medical School, USA", specialty: "Interventional Cardiology & Heart Failure" },
      { name: "Dr. Maria Rodriguez", role: "Senior Editor", institution: "Karolinska Institute, Sweden", specialty: "Cardiovascular Pharmacology & Electrophysiology" },
      { name: "Prof. Ahmed Hassan", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Cardiac Surgery & Vascular Interventions" }
    ]
  },
  jjcm: {
    fullTitle: "Japan Journal of Clinical Medicine",
    shortTitle: "JJCM",
    tagline: "SCIE-Ranked Peer-Reviewed Open Access Clinical Medicine",
    description: `Welcome to the Japan Journal of Clinical Medicine (JJCM) — an SCIE-ranked peer-reviewed, open access journal that publishes original translational, preclinical and clinical research, systematic and narrative reviews, meta-analyses, protocol studies, validation studies and case reports across all areas of medicine.

All the articles accepted for publication in the journal "Japan Journal of Clinical Medicine" are freely available online immediately upon publication.`,
    about: `The journal is an open access journal that aims to publish the most complete focuses on all types of articles such as rapidly publishing research articles, reviews, mini-reviews, case reports, short communications, letters to the editor, commentaries, and reports, providing a single forum for discussion.

The journal follows a strict double-blind peer-review process to provide unbiased recognition of the work.

JJCM welcomes direct submissions of manuscripts from authors. Manuscripts could be submitted through our online portal or as directed to the editorial office as an attachment to email at: info@iscienceqpublishers.com or isciencepublishers@gmail.com.`,
    scope: [
      { category: "Translational & Preclinical Research", topics: "Original Translational Research, Preclinical Research, Protocol Studies & Validation Studies" },
      { category: "Clinical Studies & Evidence-Based Medicine", topics: "Clinical Research, Systematic Reviews, Narrative Reviews, Meta-Analyses & Case Reports" },
      { category: "Article Formats Accepted", topics: "Research Articles, Reviews, Mini-Reviews, Case Reports, Short Communications, Letters to the Editor, Commentaries & Reports" },
      { category: "Peer-Review Protocol", topics: "Strict Double-Blind Peer-Review Process for Unbiased Recognition of Research" }
    ],
    editorialBoard: [
      { name: "Prof. Kenji Takahashi", role: "Editor-in-Chief", institution: "University of Tokyo, Japan", specialty: "Clinical Medicine & Translational Research" },
      { name: "Dr. Sarah Mitchell", role: "Associate Editor", institution: "Harvard Medical School, USA", specialty: "Preclinical Research & Protocol Studies" },
      { name: "Prof. James Chen", role: "Senior Editor", institution: "Oxford University, UK", specialty: "Meta-Analyses & Clinical Trials" },
      { name: "Dr. Yoko Tanaka", role: "Editorial Board Member", institution: "Kyoto University, Japan", specialty: "Systematic Reviews & Internal Medicine" }
    ]
  },
  ijscrm: {
    fullTitle: "International Journal of Stem Cell Research and Medicine",
    shortTitle: "IJSCRM",
    tagline: "Peer-Reviewed Open Access Stem Cell & Regenerative Medicine Research",
    description: `Welcome to the International Journal of Stem Cell Research and Medicine (IJSCRM) — a peer-reviewed, open-access journal dedicated to the advancement of stem cell research and its applications in medicine.

All the articles accepted for publication in the journal "International Journal of Stem Cell Research and Medicine" are freely available online immediately upon publication.`,
    about: `International Journal of Stem Cell Research and Medicine will feature original research, review papers, clinical studies, editorials, expert opinion and perspective papers, commentaries, and book reviews.

IJSCRM welcomes direct submissions of manuscripts from authors. Manuscripts could be submitted through our online portal or as directed to the editorial office as an attachment to email at: info@iscienceqpublishers.com or isciencepublishers@gmail.com.`,
    scope: [
      { category: "Stem Cell Biology & Pluripotency", topics: "Cancer Stem Cells, Murine Embryonic Stem Cells, Human Embryonic Stem Cells, Pluripotent Cells, Adult Stem Cells, Embryonic Stem Cells, Fetal Stem Cells" },
      { category: "Tissue Engineering & Regenerative Medicine", topics: "Regenerative Medicine, Stem Cell and Organ Regeneration, Hair Stem Cell, Endothelial Cells, Pancreatic Stem Cells" },
      { category: "Therapy & Clinical Transplantation", topics: "Stem Cell Therapy, Bone Marrow Stem Cells, Neural stem cells, Stem-Cell Transplantation, Hematopoietic Stem Cell Transplantation, Transplantation Medicine" },
      { category: "Technologies & Molecular Biology", topics: "Stem Cell Technologies & Molecular Mechanisms" },
      { category: "Article Formats Accepted", topics: "Original Research, Review Papers, Clinical Studies, Editorials, Expert Opinion & Perspective Papers, Commentaries, and Book Reviews" }
    ],
    editorialBoard: [
      { name: "Dr. Sarah Mitchell", role: "Editor-in-Chief", institution: "Harvard Medical School, USA", specialty: "Stem Cell Biology & Regenerative Medicine" },
      { name: "Prof. James Chen", role: "Associate Editor", institution: "Oxford University, UK", specialty: "Stem Cell Therapy & Transplantation" },
      { name: "Dr. Maria Rodriguez", role: "Senior Editor", institution: "Karolinska Institute, Sweden", specialty: "Pluripotent Stem Cells & Molecular Genetics" },
      { name: "Prof. Ahmed Hassan", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Cancer Stem Cells & Tissue Engineering" }
    ]
  },
  ijppr: {
    fullTitle: "International Journal of Psychiatry and Psychology Research",
    shortTitle: "IJPPR",
    tagline: "Peer-Reviewed Open Access Psychiatry & Psychology Research",
    description: `Welcome to the International Journal of Psychiatry and Psychology Research (IJPPR) — an open access, peer-reviewed journal that considers articles on all aspects of Psychiatry, Mental Disorders, Health Promotion, Health-Related Quality of Life, Mental Health Illiteracy, Behavioural Science, Biological Psychiatry, Child and Adolescent Psychiatry, Mental Health Morbidity, Mental Health Needs, Mental Health Services, Neuropsychiatry, Pathophysiology, Addiction Psychiatry, Adolescent Psychiatry, Child Psychiatry, Psychiatric Treatment, Clinical Psychiatry, Community Psychiatry, Psychiatry Diseases, Psycho Trauma, Psychotherapy, Neuroscience, Neurobiology and Genetic aspects of childhood disorders.

All the articles accepted for publication in the journal "International Journal of Psychiatry and Psychology Research" are freely available online immediately upon publication.`,
    about: `International Journal of Psychiatry and Psychology Research accepts almost all types of write-ups like research articles, reviews, case reports, case studies, commentaries, letters to editors, mini-reviews, opinions, short communication, book review, editorials, etc covering all aspects of Psychiatry.

IJPPR welcomes direct submissions of manuscripts from authors. Manuscripts could be submitted through our online portal or as directed to the editorial office as an attachment to email at: info@iscienceqpublishers.com or isciencepublishers@gmail.com.`,
    scope: [
      { category: "Clinical & Biological Psychiatry", topics: "Biological Psychiatry, Clinical Psychiatry, Neuropsychiatry, Pathophysiology & Psychiatric Treatment" },
      { category: "Child & Adolescent Psychiatry", topics: "Child and Adolescent Psychiatry, Adolescent Psychiatry, Child Psychiatry & Genetic aspects of childhood disorders" },
      { category: "Mental Health Services & Community Care", topics: "Mental Health Services, Mental Health Needs, Mental Health Morbidity, Community Psychiatry, Health Promotion & Quality of Life" },
      { category: "Psychotherapy, Behavioral Science & Neuroscience", topics: "Psychotherapy, Psycho Trauma, Behavioural Science, Neuroscience, Neurobiology & Addiction Psychiatry" },
      { category: "Types of Write-Ups Accepted", topics: "Research Articles, Reviews, Case Reports, Case Studies, Commentaries, Letters to Editors, Mini-Reviews, Opinions, Short Communications, Book Reviews & Editorials" }
    ],
    editorialBoard: [
      { name: "Dr. Sarah Mitchell", role: "Editor-in-Chief", institution: "Harvard Medical School, USA", specialty: "Psychiatry & Behavioral Science" },
      { name: "Prof. James Chen", role: "Associate Editor", institution: "Oxford University, UK", specialty: "Neuropsychiatry & Biological Psychiatry" },
      { name: "Dr. Maria Rodriguez", role: "Senior Editor", institution: "Karolinska Institute, Sweden", specialty: "Child & Adolescent Psychiatry" },
      { name: "Prof. Ahmed Hassan", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Clinical Psychology & Psychotherapy" }
    ]
  },
  ijmcrr: {
    fullTitle: "International Journal of Medical - Clinical Research & Reviews",
    shortTitle: "IJMCRR",
    tagline: "Peer-Reviewed Open Access Medical & Clinical Research",
    description: `Welcome to the International Journal of Medical - Clinical Research & Reviews (IJMCRR) — an international, fully peer-reviewed academic journal dedicated to publishing the most comprehensive and reliable research in medicine and science research.

All articles accepted for publication in the journal "International Journal of Medical - Clinical Research & Reviews" are freely available online immediately upon publication.`,
    about: `IJMCRR is an Open Access Scientific Journal that offers an interesting publishing platform globally and aims to keep scientists, clinicians and medical practitioners, researchers, and students informed and updated on ongoing research in the relevant area.

The journal purposes to publish case reports, original research, and reviews of different fields of research, which improves the health of the people globally and also shares the knowledge through different kinds of Medical - Clinical Research & Reviews.

IJMCRR welcomes direct submissions of manuscripts from authors. Manuscripts could be submitted through our online portal or as directed to the editorial office as an attachment to email at: info@iscienceqpublishers.com or isciencepublishers@gmail.com.`,
    scope: [
      { category: "Cardiovascular Science & Cardiology", topics: "Cardiology, Vascular Medicine, Interventions & Heart Health" },
      { category: "Neurology & Brain Research", topics: "Neurology, Neuro-oncology, Stroke & Cognitive Neuroscience" },
      { category: "Obstetrics & Gynecology", topics: "Women's Health, Reproductive Biology, Maternal-Fetal Medicine & Obstetrics" },
      { category: "Surgery & Operative Care", topics: "General Surgery, Orthopedics, Trauma Surgery & Surgical Interventions" },
      { category: "Internal Medicine & Critical Care", topics: "Internal Medicine, Critical Care, Emergency Medicine & Rheumatology" },
      { category: "Infectious Diseases & HIV/AIDS", topics: "Infectious Diseases, HIV/AIDS, Virology, Immunology & Inflammatory Disease" },
      { category: "Molecular Medicine & Clinical Genetics", topics: "Molecular Medicine, Clinical Genetics, Clinical Intervention, Stem Cell & Biomedicine" },
      { category: "Oncology, Nephrology & Hematology", topics: "Oncology, Nephrology, Hematology, Diabetes & Metabolic Diseases" },
      { category: "Pharmacology, Radiology & Gastroenterology", topics: "Pharmacology, Diagnostic Radiology, Gastroenterology & Therapeutics" }
    ],
    editorialBoard: [
      { name: "Dr. Sarah Mitchell", role: "Editor-in-Chief", institution: "Harvard Medical School, USA", specialty: "Internal Medicine & Clinical Research" },
      { name: "Prof. James Chen", role: "Associate Editor", institution: "Oxford University, UK", specialty: "Cardiology & Vascular Medicine" },
      { name: "Dr. Maria Rodriguez", role: "Senior Editor", institution: "Karolinska Institute, Sweden", specialty: "Clinical Genetics & Oncology" },
      { name: "Prof. Ahmed Hassan", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Surgery & Emergency Medicine" }
    ]
  },
  ijcm: {
    fullTitle: "International Journal of Clinical Medicine",
    shortTitle: "IJCM",
    tagline: "Advancing Clinical Practice Through Evidence-Based Research",
    description: `Welcome to the International Journal of Clinical Medicine (IJCM) — a premier global platform dedicated to the rapid dissemination of clinical medicine, patient care, and medical diagnostics research.

Our mission is to foster the dissemination of innovative studies, clinical insights, and evidence-based practices that contribute to improved patient outcomes and healthcare delivery. By bridging the gap between medical research and clinical application, we aim to accelerate the growth of modern medicine worldwide.`,
    about: `IJCM publishes high-quality, impactful research across the full breadth of clinical medicine. We embrace interdisciplinary research, combining clinical practice with translational medicine, digital health models, and real-world patient outcomes analysis.

To maintain scientific integrity and ethical excellence, every manuscript undergoes a rigorous peer review process. We have optimized our workflow to ensure that groundbreaking clinical research reaches the global academic community through a seamless and efficient publication pathway.`,
    scope: [
      { category: "Internal Medicine", topics: "Cardiology, Pulmonology, Gastroenterology & Endocrinology" },
      { category: "Surgical Sciences", topics: "General Surgery, Orthopedics, Neurosurgery & Transplantation" },
      { category: "Diagnostic Medicine", topics: "Radiology, Pathology, Laboratory Medicine & Imaging" },
      { category: "Emergency Medicine", topics: "Critical Care, Trauma, Acute Care & Resuscitation" },
      { category: "Specialized Care", topics: "Oncology, Geriatrics, Pediatrics & Women's Health" },
      { category: "Healthcare Innovation", topics: "Telemedicine, AI in Diagnostics & Precision Medicine" }
    ],
    editorialBoard: [
      { name: "Dr. Sarah Mitchell", role: "Editor-in-Chief", institution: "Harvard Medical School, USA", specialty: "Internal Medicine" },
      { name: "Prof. James Chen", role: "Associate Editor", institution: "Oxford University, UK", specialty: "Cardiology" },
      { name: "Dr. Maria Rodriguez", role: "Senior Editor", institution: "Karolinska Institute, Sweden", specialty: "Clinical Research" },
      { name: "Prof. Ahmed Hassan", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Emergency Medicine" }
    ]
  },
  jps: {
    fullTitle: "Journal of Pharmaceutical Sciences",
    shortTitle: "JPS",
    tagline: "Driving Innovation in Drug Discovery & Development",
    description: `Welcome to the Journal of Pharmaceutical Sciences (JPS) — a leading international platform dedicated to advancing pharmaceutical research, drug discovery, and pharmacology.

Our mission is to promote the dissemination of cutting-edge research in pharmaceutical sciences that contributes to the development of safer and more effective therapeutic interventions. We bridge the gap between laboratory research and clinical drug development.`,
    about: `JPS publishes high-quality research spanning the entire spectrum of pharmaceutical sciences. We encourage interdisciplinary approaches that combine pharmaceutical chemistry, pharmacology, and drug delivery systems with emerging technologies.

Every manuscript undergoes rigorous peer review to ensure scientific excellence and integrity. Our streamlined publication process ensures rapid dissemination of important pharmaceutical discoveries to the global scientific community.`,
    scope: [
      { category: "Drug Discovery", topics: "Target Identification, Lead Optimization & Structure-Activity" },
      { category: "Pharmaceutical Chemistry", topics: "Medicinal Chemistry, Natural Products & Synthesis" },
      { category: "Pharmacology", topics: "Pharmacokinetics, Pharmacodynamics & Toxicology" },
      { category: "Drug Delivery", topics: "Formulation, Nanotechnology & Controlled Release" },
      { category: "Clinical Pharmacy", topics: "Pharmacotherapy, Drug Interactions & Patient Safety" },
      { category: "Regulatory Sciences", topics: "Quality Control, GMP & Regulatory Affairs" }
    ],
    editorialBoard: [
      { name: "Prof. David Kim", role: "Editor-in-Chief", institution: "Seoul National University, South Korea", specialty: "Pharmaceutical Chemistry" },
      { name: "Dr. Emma Thompson", role: "Associate Editor", institution: "University of Melbourne, Australia", specialty: "Drug Delivery" },
      { name: "Prof. Michael Brown", role: "Senior Editor", institution: "MIT, USA", specialty: "Pharmacology" },
      { name: "Dr. Lisa Wang", role: "Editorial Board Member", institution: "Peking University, China", specialty: "Natural Products" }
    ]
  },
  als: {
    fullTitle: "Advances in Life Sciences",
    shortTitle: "ALS",
    tagline: "Exploring the Frontiers of Biological Research",
    description: `Welcome to Advances in Life Sciences (ALS) — a premier global platform dedicated to publishing groundbreaking research in molecular biology, genetics, and biotechnology.

Our mission is to advance scientific understanding of life at all levels, from molecular mechanisms to ecosystem dynamics. We aim to accelerate discoveries that address global challenges in health, agriculture, and environmental sustainability.`,
    about: `ALS publishes high-quality research across all areas of life sciences. We embrace interdisciplinary approaches that integrate molecular biology, genetics, biochemistry, and computational biology.

Every manuscript undergoes rigorous peer review by experts in the field. Our efficient publication process ensures rapid dissemination of important discoveries to the global scientific community.`,
    scope: [
      { category: "Molecular Biology", topics: "Gene Expression, Protein Function & Cell Signaling" },
      { category: "Genetics & Genomics", topics: "Genome Analysis, Epigenetics & Population Genetics" },
      { category: "Biotechnology", topics: "Genetic Engineering, Synthetic Biology & Bioinformatics" },
      { category: "Cell Biology", topics: "Cell Structure, Development & Stem Cells" },
      { category: "Biochemistry", topics: "Enzymology, Metabolism & Structural Biology" },
      { category: "Systems Biology", topics: "Network Analysis, Computational Modeling & Omics" }
    ],
    editorialBoard: [
      { name: "Prof. Jennifer Lee", role: "Editor-in-Chief", institution: "Stanford University, USA", specialty: "Molecular Biology" },
      { name: "Dr. Robert Chen", role: "Associate Editor", institution: "Cambridge University, UK", specialty: "Genetics" },
      { name: "Prof. Anna Schmidt", role: "Senior Editor", institution: "Max Planck Institute, Germany", specialty: "Cell Biology" },
      { name: "Dr. Yuki Tanaka", role: "Editorial Board Member", institution: "University of Tokyo, Japan", specialty: "Biotechnology" }
    ]
  },
  ghmr: {
    fullTitle: "Global Health & Medical Research",
    shortTitle: "GHMR",
    tagline: "Addressing Global Health Challenges Through Research",
    description: `Welcome to Global Health & Medical Research (GHMR) — a leading international platform dedicated to publishing research that addresses worldwide health challenges and promotes evidence-based healthcare solutions.

Our mission is to foster collaboration among researchers, clinicians, and public health professionals to tackle global health issues. We aim to bridge the gap between research and policy to improve health outcomes worldwide.`,
    about: `GHMR publishes high-quality research addressing global health challenges, including infectious diseases, non-communicable diseases, health systems, and health equity. We embrace multidisciplinary approaches that combine epidemiology, public health, and clinical research.

Every manuscript undergoes rigorous peer review to ensure scientific rigor and relevance to global health priorities. Our efficient publication process ensures rapid dissemination of important findings to inform public health practice and policy.`,
    scope: [
      { category: "Infectious Diseases", topics: "Epidemiology, Prevention, Treatment & Emerging Pathogens" },
      { category: "Non-Communicable Diseases", topics: "Cardiovascular, Cancer, Diabetes & Mental Health" },
      { category: "Health Systems", topics: "Healthcare Delivery, Policy, Economics & Quality" },
      { category: "Public Health", topics: "Health Promotion, Disease Prevention & Environmental Health" },
      { category: "Global Health Equity", topics: "Health Disparities, Access & Social Determinants" },
      { category: "Health Innovation", topics: "Digital Health, mHealth & Health Technology Assessment" }
    ],
    editorialBoard: [
      { name: "Dr. Patricia Nguyen", role: "Editor-in-Chief", institution: "Johns Hopkins University, USA", specialty: "Epidemiology" },
      { name: "Prof. Carlos Martinez", role: "Associate Editor", institution: "University of São Paulo, Brazil", specialty: "Public Health" },
      { name: "Dr. Fatima Al-Hassan", role: "Senior Editor", institution: "WHO Collaborating Centre, Switzerland", specialty: "Health Policy" },
      { name: "Prof. John Williams", role: "Editorial Board Member", institution: "London School of Hygiene, UK", specialty: "Global Health" }
    ]
  }
};

// Menu items for sidebar
const menuItems = [
  { id: "home", label: "Journal Home", icon: House },
  { id: "guidelines", label: "Author Guidelines", icon: PencilLine },
  { id: "editorial", label: "Editorial Board", icon: UsersFour },
  { id: "inpress", label: "Inpress", icon: Printer },
  { id: "current", label: "Current Issue", icon: BookOpen },
  { id: "archives", label: "Archives", icon: Archive },
  { id: "submit", label: "Submit Manuscript", icon: FileText }
];

export default function JournalDetailPage() {
  const { journalId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const activeSection = searchParams.get("section") || "home";

  const currentArticles = useMemo(() => JOURNAL_ARTICLES[journalId] || [], [journalId]);
  const [selectedPdfArticle, setSelectedPdfArticle] = useState(null);
  const [inlinePdfOpen, setInlinePdfOpen] = useState(false);
  const [expandedAbstracts, setExpandedAbstracts] = useState({ "ijmcrr-v1-i1-01": true });

  const toggleAbstract = (id) => {
    setExpandedAbstracts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (searchParams.get("view") === "pdf" && currentArticles.length > 0) {
      setSelectedPdfArticle(currentArticles[0]);
    }
  }, [searchParams, currentArticles]);

  const setActiveSection = (section) => {
    setSearchParams({ section });
  };

  useEffect(() => {
    const fetchJournal = async () => {
      const fallback = DEFAULT_JOURNALS.find(j => j.id === journalId) || DEFAULT_JOURNALS[0];
      if (!API) {
        setJournal(fallback);
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/journals/${journalId}`);
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data) && response.data.id) {
          setJournal(response.data);
        } else {
          setJournal(fallback);
        }
      } catch (error) {
        console.error("Failed to fetch journal:", error);
        setJournal(fallback);
      } finally {
        setLoading(false);
      }
    };
    fetchJournal();
  }, [journalId]);

  const details = journalDetails[journalId] || journalDetails.ijcm;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#64748B]">Loading...</div>
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Journal Not Found</h2>
          <Link to="/journals" className="text-[#0D9488] hover:underline">
            Back to Journals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="journal-detail-page">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/30 to-[#1E40AF]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-4">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-[#94A3B8] hover:text-white transition-colors">Home</Link>
              </li>
              <li className="text-[#64748B]">/</li>
              <li>
                <Link to="/journals" className="text-[#94A3B8] hover:text-white transition-colors">Journals</Link>
              </li>
              <li className="text-[#64748B]">/</li>
              <li className="text-[#0D9488]">Details</li>
            </ol>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/journals"
              className="inline-flex items-center gap-2 text-[#0D9488] hover:text-[#5EEAD4] mb-4 transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Back to Journals
            </Link>
            
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Chivo'] tracking-tight leading-tight mb-2">
              {details.fullTitle}
            </h1>
            <p className="text-lg text-[#0D9488] font-medium mb-4">
              {details.tagline}
            </p>
            
            {/* Journal Meta */}
            <div className="flex flex-wrap gap-4 sm:gap-6">
              <div className="flex items-center gap-2 text-[#CBD5E1]">
                <Calendar size={18} className="text-[#0D9488]" />
                <span className="text-sm">{journal.frequency}</span>
              </div>
              <div className="flex items-center gap-2 text-[#CBD5E1]">
                <ChartLine size={18} className="text-[#0D9488]" />
                <span className="text-sm">IF: {journal.impact_factor}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-8 md:py-12 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar Menu */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
                <div className="bg-[#0F172A] text-white px-4 py-3">
                  <h3 className="font-semibold font-['Chivo']">Menu</h3>
                </div>
                <nav className="p-2">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => item.id === "submit" ? window.location.href = "/submit-manuscript" : setActiveSection(item.id)}
                      data-testid={`menu-${item.id}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-left transition-all ${
                        activeSection === item.id
                          ? "bg-[#1E40AF] text-white"
                          : "text-[#334155] hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <item.icon size={20} weight={activeSection === item.id ? "fill" : "regular"} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 md:p-8">
                {/* Journal Home Section */}
                {activeSection === "home" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="section-home"
                  >
                    <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] mb-6">
                      {details.fullTitle} ({details.shortTitle})
                    </h2>
                    
                    <div className="prose prose-lg max-w-none text-[#334155]">
                      {details.description.split('\n\n').map((para, i) => (
                        <p key={i} className="mb-4 leading-relaxed">{para}</p>
                      ))}
                    </div>

                    <div className="mt-8">
                      <h3 className="text-xl font-bold text-[#0F172A] font-['Chivo'] mb-4">
                        About This Journal
                      </h3>
                      <div className="prose prose-lg max-w-none text-[#334155]">
                        {details.about.split('\n\n').map((para, i) => (
                          <p key={i} className="mb-4 leading-relaxed">{para}</p>
                        ))}
                      </div>
                    </div>

                    {/* Latest Published Article Banner */}
                    {currentArticles.length > 0 && (
                      <div className="mt-8 bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white p-6 rounded-sm shadow-md border-l-4 border-[#0D9488]">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <span className="bg-[#0D9488] text-white text-xs font-semibold px-2.5 py-0.5 rounded-sm uppercase tracking-wide">
                            Latest Published Article
                          </span>
                          <span className="text-xs text-slate-300">
                            Volume {currentArticles[0].volume}, Issue {currentArticles[0].issue} ({currentArticles[0].month} {currentArticles[0].year})
                          </span>
                        </div>
                        <h4 className="text-lg sm:text-xl font-bold font-['Chivo'] text-white mt-2 mb-2">
                          {currentArticles[0].title}
                        </h4>
                        <p className="text-xs text-[#CBD5E1] mb-4">
                          {currentArticles[0].authors.map(a => a.name).join(", ")} • Published: {currentArticles[0].publishedDate}
                          {currentArticles[0].doi && (
                            <>
                              {" • "}
                              <span>
                                DOI:{" "}
                                <a
                                  href={`https://doi.org/${currentArticles[0].doi}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-teal-300 hover:underline"
                                >
                                  {currentArticles[0].doi}
                                </a>
                              </span>
                            </>
                          )}
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setSelectedPdfArticle(currentArticles[0])}
                            className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0f766e] text-white px-4 py-2 rounded-sm font-medium text-xs sm:text-sm transition-colors cursor-pointer"
                          >
                            <Eye size={16} weight="bold" />
                            View PDF Online
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveSection("current")}
                            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-sm font-medium text-xs sm:text-sm transition-colors cursor-pointer border border-white/20"
                          >
                            <BookOpen size={16} />
                            Read Article & Abstract
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Review Timelines */}
                    <div className="mt-8 bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm p-6">
                      <h3 className="text-lg font-bold text-[#0F172A] font-['Chivo'] mb-4 flex items-center gap-2">
                        <Clock size={20} className="text-[#0D9488]" />
                        Optimized Review Timelines
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="flex items-center gap-3">
                          <CheckCircle size={18} className="text-[#0D9488] flex-shrink-0" weight="fill" />
                          <span className="text-sm"><strong>Initial Screening:</strong> 3-5 Days</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle size={18} className="text-[#0D9488] flex-shrink-0" weight="fill" />
                          <span className="text-sm"><strong>Rapid Publication:</strong> 10-15 Days</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle size={18} className="text-[#0D9488] flex-shrink-0" weight="fill" />
                          <span className="text-sm"><strong>Standard Review:</strong> 25-30 Days</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle size={18} className="text-[#0D9488] flex-shrink-0" weight="fill" />
                          <span className="text-sm"><strong>Final Publication:</strong> 5-7 Days</span>
                        </div>
                      </div>
                    </div>

                    {/* Journal Scope */}
                    <div className="mt-8">
                      <h3 className="text-xl font-bold text-[#0F172A] font-['Chivo'] mb-4">
                        Journal Scope
                      </h3>
                      <p className="text-[#334155] mb-4">
                        {details.shortTitle} invites original research, reviews, and clinical case reports across the following areas:
                      </p>
                      <div className="grid gap-3">
                        {details.scope.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <CheckCircle size={18} className="text-[#0D9488] flex-shrink-0 mt-1" weight="fill" />
                            <div>
                              <span className="font-semibold text-[#0F172A]">{item.category}:</span>
                              <span className="text-[#64748B] ml-1">{item.topics}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Why Publish Section */}
                    <div className="mt-8">
                      <h3 className="text-xl font-bold text-[#0F172A] font-['Chivo'] mb-4">
                        Why Publish with {details.shortTitle}?
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {[
                          { title: "Accelerated Visibility", desc: "Instant Open-Access for global readership" },
                          { title: "Streamlined Evaluation", desc: "Expert-driven editorial review and technical audit" },
                          { title: "Rapid Publication", desc: "Efficient submission-to-publication timelines" },
                          { title: "Global Reach", desc: "DOI assignment and indexing in major databases" },
                          { title: "Author-Centric Support", desc: "Professional formatting and dedicated communication" },
                          { title: "Competitive APC", desc: "Affordable fees designed to support researchers" }
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <CheckCircle size={18} className="text-[#0D9488] flex-shrink-0 mt-1" weight="fill" />
                            <div>
                              <span className="font-semibold text-[#0F172A]">{item.title}</span>
                              <p className="text-sm text-[#64748B]">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Submit CTA */}
                    <div className="mt-10 bg-gradient-to-r from-[#0D9488] to-[#1E40AF] rounded-sm p-6 text-white">
                      <h3 className="font-bold text-xl font-['Chivo'] mb-2">Submit Your Manuscript</h3>
                      <p className="text-white/80 mb-4">
                        Join the global network of professionals and researchers shaping the future of {details.shortTitle.includes("Clinical") ? "clinical medicine" : "science"}.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Link
                          to="/submit-manuscript"
                          className="inline-flex items-center gap-2 bg-white text-[#0D9488] hover:bg-[#F0FDFA] px-5 py-2 rounded-sm font-medium transition-colors"
                        >
                          Online Submission
                          <ArrowRight size={16} weight="bold" />
                        </Link>
                        <a
                          href="mailto:info@iscienceqpublishers.com"
                          className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 px-5 py-2 rounded-sm font-medium transition-colors text-xs sm:text-sm"
                        >
                          <EnvelopeSimple size={16} />
                          info@iscienceqpublishers.com
                        </a>
                        <a
                          href="mailto:isciencepublishers@gmail.com"
                          className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 px-5 py-2 rounded-sm font-medium transition-colors text-xs sm:text-sm"
                        >
                          <EnvelopeSimple size={16} />
                          isciencepublishers@gmail.com
                        </a>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Author Guidelines Section */}
                {activeSection === "guidelines" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="section-guidelines"
                  >
                    <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] mb-6">
                      Author Guidelines
                    </h2>
                    <p className="text-[#334155] mb-8">
                      We welcome submissions from researchers worldwide. Please review our guidelines carefully before submitting your manuscript.
                    </p>

                    {/* Guidelines Cards */}
                    <div className="grid md:grid-cols-2 gap-4 mb-10">
                      {[
                        { title: "Instructions to Authors", desc: "Manuscript preparation, structure, and ethical requirements.", link: "#instructions" },
                        { title: "Publication Process", desc: "Step-by-step workflow from submission to publication.", link: "#process" },
                        { title: "Submission Process", desc: "How to submit manuscripts and required documents.", link: "#submission" },
                        { title: "Publication Ethics", desc: "Integrity, COPE compliance, and misconduct handling.", link: "#ethics" }
                      ].map((card, i) => (
                        <div key={i} className="border border-[#E2E8F0] rounded-sm p-5 hover:shadow-md transition-shadow">
                          <h4 className="font-semibold text-[#0F172A] mb-2">{card.title}</h4>
                          <p className="text-sm text-[#64748B] mb-3">{card.desc}</p>
                          <span className="text-[#0D9488] text-sm font-medium">View Guideline →</span>
                        </div>
                      ))}
                    </div>

                    {/* Instructions to Authors */}
                    <div id="instructions" className="mb-10">
                      <h3 className="text-xl font-bold text-[#0F172A] font-['Chivo'] mb-4 pb-2 border-b border-[#E2E8F0]">
                        Instructions to Authors
                      </h3>
                      
                      <div className="space-y-6 text-[#334155]">
                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">1. Manuscript Preparation Guidelines</h4>
                          <p>Authors must ensure that their manuscripts meet basic quality and formatting standards required for evaluation. Submissions that lack clarity or do not follow these guidelines may be returned for revision before entering editorial review.</p>
                        </div>

                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">2. Article Types and Format</h4>
                          <p className="mb-3">{details.shortTitle} accepts the following article types:</p>
                          <ul className="list-disc list-inside space-y-1 ml-4">
                            <li><strong>Original / Research Articles</strong> – Present novel and unpublished findings with detailed methodology, data analysis, and conclusions.</li>
                            <li><strong>Review Articles</strong> – Summarize and analyze previous studies, offering insight and future perspectives.</li>
                            <li><strong>Case Reports</strong> – Describe rare or significant clinical or scientific cases with clear observations.</li>
                            <li><strong>Editorials</strong> – Expert opinions or editor reflections on current topics.</li>
                            <li><strong>Commentaries</strong> – Scholarly discussions, critiques, or alternative viewpoints.</li>
                            <li><strong>Letters to the Editor</strong> – Short, constructive opinions or responses to published content.</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">3. Manuscript Structure</h4>
                          <p className="mb-3">All manuscripts should be organized in the following order:</p>
                          <ol className="list-decimal list-inside space-y-1 ml-4">
                            <li>Cover Letter</li>
                            <li>Title Page</li>
                            <li>Abstract and Keywords</li>
                            <li>Main Text</li>
                            <li>Acknowledgments</li>
                            <li>References</li>
                            <li>Tables and Figures</li>
                          </ol>
                        </div>

                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">4. References</h4>
                          <p>References should be cited in numerical order using superscript numbers in the text. When citing six or fewer authors, list all names. For seven or more, list the first three followed by et al.</p>
                        </div>

                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">5. Tables and Figures</h4>
                          <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Place tables after references and number them consecutively.</li>
                            <li>Submit figures separately as high-resolution (.jpg or .tif) files, minimum 300 dpi.</li>
                            <li>Include clear legends and define abbreviations.</li>
                            <li>Obtain permission for any previously published figures.</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">6. Copyright and Licensing</h4>
                          <p>Upon acceptance, authors retain copyright and grant iScience Publishers the right to publish under the Creative Commons Attribution License (CC BY), allowing free use and distribution with proper citation.</p>
                        </div>

                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">7. Ethical Considerations</h4>
                          <p>iScience Publishers adheres to the Committee on Publication Ethics (COPE) guidelines. Authors must disclose conflicts of interest, obtain ethical approval for studies involving humans or animals, and ensure originality. All manuscripts are screened for plagiarism before review.</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Editorial Board Section */}
                {activeSection === "editorial" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="section-editorial"
                  >
                    <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] mb-6">
                      Editorial Board
                    </h2>
                    <p className="text-[#334155] mb-8">
                      Our editorial board comprises distinguished scholars and researchers from leading institutions worldwide, ensuring the highest standards of scientific excellence.
                    </p>

                    <div className="grid gap-6">
                      {details.editorialBoard.map((member, i) => (
                        <div key={i} className="flex gap-4 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm">
                          <div className="w-16 h-16 bg-[#0D9488] rounded-full flex items-center justify-center text-white font-bold text-xl">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <h4 className="font-semibold text-[#0F172A]">{member.name}</h4>
                            <p className="text-[#0D9488] text-sm font-medium">{member.role}</p>
                            <p className="text-[#64748B] text-sm">{member.institution}</p>
                            <p className="text-[#94A3B8] text-sm">Specialty: {member.specialty}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 p-6 bg-[#F0FDFA] border border-[#0D9488]/20 rounded-sm">
                      <h4 className="font-semibold text-[#0F172A] mb-2">Join Our Editorial Board</h4>
                      <p className="text-[#64748B] mb-4">
                        We welcome applications from qualified researchers interested in serving on our editorial board.
                      </p>
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-[#0D9488] font-medium hover:text-[#0F766E]"
                      >
                        Contact Us to Apply
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* Inpress Section */}
                {activeSection === "inpress" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="section-inpress"
                  >
                    <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] mb-6">
                      Inpress Articles
                    </h2>
                    <p className="text-[#334155] mb-8">
                      Articles that have been accepted for publication and are awaiting final formatting and release.
                    </p>

                    <div className="text-center py-12 bg-[#F8FAFC] rounded-sm border border-[#E2E8F0]">
                      <Printer size={48} className="text-[#CBD5E1] mx-auto mb-4" />
                      <p className="text-[#64748B]">No articles currently in press.</p>
                      <p className="text-sm text-[#94A3B8] mt-2">Check back soon for upcoming publications.</p>
                    </div>

                    <div className="mt-8 p-6 bg-gradient-to-r from-[#0D9488] to-[#1E40AF] rounded-sm text-white">
                      <h4 className="font-semibold text-lg mb-2">Submit Your Research</h4>
                      <p className="text-white/80 mb-4">Be among the first to publish in our upcoming issues.</p>
                      <Link
                        to="/submit-manuscript"
                        className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-4 py-2 rounded-sm font-medium hover:bg-[#F0FDFA] transition-colors"
                      >
                        Submit Manuscript
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* Current Issue Section */}
                {activeSection === "current" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="section-current"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-[#E2E8F0]">
                      <div>
                        <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">
                          Current Issue
                        </h2>
                        <div className="flex items-center gap-2 text-[#64748B] mt-1 text-sm">
                          <Calendar size={18} className="text-[#0D9488]" />
                          <span>
                            {currentArticles.length > 0 
                              ? `Volume ${currentArticles[0].volume}, Issue ${currentArticles[0].issue} | ${currentArticles[0].month} ${currentArticles[0].year}`
                              : "Volume 1, Issue 1 | 2026"}
                          </span>
                        </div>
                      </div>
                      {currentArticles.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Published Issue
                          </span>
                        </div>
                      )}
                    </div>

                    {currentArticles.length > 0 ? (
                      <div className="space-y-8">
                        {currentArticles.map((article) => (
                          <div
                            key={article.id}
                            className="bg-white border border-[#CBD5E1] rounded-sm p-6 sm:p-7 shadow-sm hover:shadow-md transition-shadow"
                          >
                            {/* Badges & Meta */}
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <span className="bg-[#0D9488] text-white text-xs font-semibold px-2.5 py-0.5 rounded-sm">
                                {article.articleType}
                              </span>
                              <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded-sm">
                                Open Access
                              </span>
                              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-sm">
                                Peer Reviewed
                              </span>
                              <span className="text-xs text-[#64748B] sm:ml-auto">
                                Vol. {article.volume} | Issue {article.issue} | pp. {article.pages} ({article.year})
                              </span>
                            </div>

                            {/* Article Title */}
                            <h3 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-['Chivo'] leading-snug mb-3">
                              {article.title}
                            </h3>

                            {/* Authors */}
                            <div className="text-sm text-[#334155] mb-2 font-medium">
                              {article.authors.map((author, idx) => (
                                <span key={author.name}>
                                  {author.name}
                                  {author.isCorresponding && (
                                    <sup className="text-[#0D9488] font-bold text-xs" title="Corresponding Author"> *</sup>
                                  )}
                                  {idx < article.authors.length - 1 ? ", " : ""}
                                </span>
                              ))}
                            </div>

                            {/* Affiliation */}
                            <p className="text-xs text-[#64748B] italic mb-3">
                              {article.affiliations}
                            </p>

                            {/* Metadata Dates */}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-[#64748B] bg-[#F8FAFC] p-3 rounded border border-[#E2E8F0] mb-5">
                              <span><strong>Received:</strong> {article.receivedDate}</span>
                              <span className="text-slate-300">•</span>
                              <span><strong>Accepted:</strong> {article.acceptedDate}</span>
                              <span className="text-slate-300">•</span>
                              <span><strong>Published:</strong> {article.publishedDate}</span>
                              {article.doi && (
                                <>
                                  <span className="text-slate-300">•</span>
                                  <span>
                                    <strong>DOI:</strong>{" "}
                                    <a
                                      href={`https://doi.org/${article.doi}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#0D9488] hover:underline font-semibold"
                                    >
                                      {article.doi}
                                    </a>
                                  </span>
                                </>
                              )}
                            </div>

                            {/* Abstract */}
                            <div className="mb-5">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-[#0F172A]">
                                  Abstract
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => toggleAbstract(article.id)}
                                  className="text-xs text-[#0D9488] font-medium hover:underline cursor-pointer"
                                >
                                  {expandedAbstracts[article.id] ? "Show Less" : "Read Full Abstract"}
                                </button>
                              </div>
                              <p className={`text-sm text-[#475569] leading-relaxed whitespace-pre-line ${expandedAbstracts[article.id] ? "" : "line-clamp-4"}`}>
                                {article.abstract}
                              </p>
                            </div>

                            {/* Keywords */}
                            {article.keywords && (
                              <div className="mb-6">
                                <span className="text-xs font-semibold text-[#0F172A] block mb-1.5">Keywords:</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {article.keywords.map((kw, i) => (
                                    <span key={i} className="text-xs bg-[#F1F5F9] text-[#475569] px-2 py-0.5 rounded border border-[#E2E8F0]">
                                      {kw}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Prominent Action Bar */}
                            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E2E8F0]">
                              <button
                                type="button"
                                onClick={() => setSelectedPdfArticle(article)}
                                className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0f766e] text-white px-4 py-2.5 rounded-sm font-medium text-sm transition-colors shadow-sm cursor-pointer"
                              >
                                <Eye size={18} weight="bold" />
                                View PDF in Website
                              </button>

                              <button
                                type="button"
                                onClick={() => setInlinePdfOpen(prev => !prev)}
                                className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-[#0F172A] px-4 py-2.5 rounded-sm font-medium text-sm transition-colors border border-slate-300 cursor-pointer"
                              >
                                <BookOpen size={18} weight="bold" />
                                {inlinePdfOpen ? "Hide Inline Viewer" : "Preview PDF on Page"}
                              </button>

                              <a
                                href={article.pdfUrl}
                                download={article.fileName}
                                className="inline-flex items-center gap-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white px-4 py-2.5 rounded-sm font-medium text-sm transition-colors shadow-sm"
                              >
                                <DownloadSimple size={18} weight="bold" />
                                Download PDF
                              </a>

                              <a
                                href={article.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-[#0D9488] hover:underline px-2 py-1 font-medium"
                              >
                                <ArrowsOut size={14} />
                                Fullscreen Tab
                              </a>

                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(article.citation);
                                  toast.success("Citation copied to clipboard!");
                                }}
                                className="inline-flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0F172A] px-2 py-1 sm:ml-auto font-medium cursor-pointer"
                              >
                                <Copy size={14} />
                                Copy Citation
                              </button>
                            </div>

                            {/* Inline PDF Viewer Component */}
                            {inlinePdfOpen && (
                              <div className="mt-6 border-2 border-[#0D9488]/30 rounded overflow-hidden shadow-inner bg-slate-900">
                                <div className="bg-[#0F172A] text-white px-4 py-2.5 text-xs flex items-center justify-between">
                                  <span className="font-semibold flex items-center gap-2">
                                    <FilePdf size={18} className="text-[#0D9488]" weight="fill" />
                                    Inline PDF Reader • {article.title}
                                  </span>
                                  <div className="flex items-center gap-3">
                                    <a
                                      href={article.pdfUrl}
                                      download={article.fileName}
                                      className="text-white hover:text-[#5EEAD4] flex items-center gap-1"
                                    >
                                      <DownloadSimple size={14} />
                                      Download
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() => setInlinePdfOpen(false)}
                                      className="text-slate-400 hover:text-white cursor-pointer"
                                    >
                                      ✕ Close
                                    </button>
                                  </div>
                                </div>
                                <iframe
                                  src={`${article.pdfUrl}#toolbar=1&navpanes=0`}
                                  title="Inline PDF Viewer"
                                  className="w-full h-[750px] bg-white border-0"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-[#F8FAFC] rounded-sm border border-[#E2E8F0]">
                        <BookOpen size={48} className="text-[#CBD5E1] mx-auto mb-4" />
                        <p className="text-[#64748B]">The current issue is being prepared.</p>
                        <p className="text-sm text-[#94A3B8] mt-2">Articles will be published soon.</p>
                      </div>
                    )}

                    <div className="mt-8 p-6 bg-gradient-to-r from-[#0D9488] to-[#1E40AF] rounded-sm text-white">
                      <h4 className="font-semibold text-lg mb-2">Contribute to Our Next Issue</h4>
                      <p className="text-white/80 mb-4">Submit your research and be featured in our upcoming publication.</p>
                      <Link
                        to="/submit-manuscript"
                        className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-4 py-2 rounded-sm font-medium hover:bg-[#F0FDFA] transition-colors"
                      >
                        Submit Manuscript
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* Archives Section */}
                {activeSection === "archives" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="section-archives"
                  >
                    <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] mb-2">
                      Archives
                    </h2>
                    <p className="text-[#334155] mb-6">
                      Browse past issues and published articles from {details.fullTitle}.
                    </p>

                    {currentArticles.length > 0 ? (
                      <div className="space-y-6">
                        <div className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-sm p-5">
                          <div className="flex items-center justify-between pb-3 border-b border-[#E2E8F0] mb-4">
                            <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                              <Archive size={20} className="text-[#0D9488]" />
                              Volume 1, Issue 1 (October 2026)
                            </h3>
                            <span className="text-xs bg-[#0D9488] text-white px-2.5 py-0.5 rounded-sm font-medium">
                              1 Article Published
                            </span>
                          </div>

                          {currentArticles.map((article) => (
                            <div key={article.id} className="bg-white border border-[#E2E8F0] p-4 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                              <div className="flex-1">
                                <span className="text-xs font-semibold text-[#0D9488] uppercase">{article.articleType}</span>
                                <h4 className="font-bold text-[#0F172A] text-base mt-1">{article.title}</h4>
                                <p className="text-xs text-[#64748B] mt-1">
                                  {article.authors.map(a => a.name).join(", ")} • Published: {article.publishedDate}
                                  {article.doi && (
                                    <>
                                      {" • "}
                                      <span>
                                        DOI:{" "}
                                        <a
                                          href={`https://doi.org/${article.doi}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[#0D9488] hover:underline"
                                        >
                                          {article.doi}
                                        </a>
                                      </span>
                                    </>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setSelectedPdfArticle(article)}
                                  className="inline-flex items-center gap-1.5 bg-[#0D9488] hover:bg-[#0f766e] text-white text-xs font-medium px-3 py-2 rounded-sm transition-colors cursor-pointer"
                                >
                                  <Eye size={16} />
                                  View PDF
                                </button>
                                <a
                                  href={article.pdfUrl}
                                  download={article.fileName}
                                  className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white text-xs font-medium px-3 py-2 rounded-sm transition-colors"
                                >
                                  <DownloadSimple size={16} />
                                  PDF
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-[#F8FAFC] rounded-sm border border-[#E2E8F0]">
                        <Archive size={48} className="text-[#CBD5E1] mx-auto mb-4" />
                        <p className="text-[#64748B]">No archived issues available yet.</p>
                        <p className="text-sm text-[#94A3B8] mt-2">This is a new journal. Archives will be available after the first issue is published.</p>
                      </div>
                    )}

                    <div className="mt-8 p-6 bg-gradient-to-r from-[#0D9488] to-[#1E40AF] rounded-sm text-white">
                      <h4 className="font-semibold text-lg mb-2">Be Part of History</h4>
                      <p className="text-white/80 mb-4">Submit your research and be featured in our inaugural issue.</p>
                      <Link
                        to="/submit-manuscript"
                        className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-4 py-2 rounded-sm font-medium hover:bg-[#F0FDFA] transition-colors"
                      >
                        Submit Manuscript
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg italic text-[#CBD5E1]">
            "{details.fullTitle} — Advancing Science, Improving Lives."
          </p>
        </div>
      </section>

      {/* Interactive In-Website PDF Viewer Modal */}
      <AnimatePresence>
        {selectedPdfArticle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-6xl bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col h-[94vh]"
            >
              {/* Modal Header */}
              <div className="bg-[#0F172A] text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-4 border-b border-slate-700 flex-shrink-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-[#0D9488] text-white text-xs font-semibold px-2 py-0.5 rounded">
                      {selectedPdfArticle.articleType || "Research Article"}
                    </span>
                    <span className="text-xs text-slate-300">
                      Volume {selectedPdfArticle.volume} | Issue {selectedPdfArticle.issue} | pp. {selectedPdfArticle.pages} ({selectedPdfArticle.year})
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-semibold text-white truncate" title={selectedPdfArticle.title}>
                    {selectedPdfArticle.title}
                  </h3>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={selectedPdfArticle.pdfUrl}
                    download={selectedPdfArticle.fileName || "article.pdf"}
                    className="inline-flex items-center gap-1.5 bg-[#0D9488] hover:bg-[#0f766e] text-white text-xs sm:text-sm font-medium px-3 py-1.5 rounded transition-colors"
                    title="Download PDF"
                  >
                    <DownloadSimple size={16} weight="bold" />
                    <span className="hidden sm:inline">Download</span>
                  </a>
                  <a
                    href={selectedPdfArticle.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs sm:text-sm font-medium px-3 py-1.5 rounded transition-colors"
                    title="Open Fullscreen in New Tab"
                  >
                    <ArrowsOut size={16} weight="bold" />
                    <span className="hidden sm:inline">Fullscreen</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => setSelectedPdfArticle(null)}
                    className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-800 transition-colors ml-1 cursor-pointer"
                    title="Close"
                  >
                    <X size={22} weight="bold" />
                  </button>
                </div>
              </div>

              {/* Modal PDF Viewer Body */}
              <div className="flex-1 bg-slate-100 relative">
                <iframe
                  src={`${selectedPdfArticle.pdfUrl}#toolbar=1&navpanes=1&zoom=100`}
                  title={selectedPdfArticle.title}
                  className="w-full h-full border-0"
                />
              </div>

              {/* Modal Footer */}
              <div className="bg-slate-50 border-t border-slate-200 px-4 py-2.5 text-xs text-slate-600 flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
                <div className="truncate max-w-2xl">
                  <strong>Cite:</strong> {selectedPdfArticle.citation}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedPdfArticle.citation);
                    toast.success("Citation copied to clipboard!");
                  }}
                  className="inline-flex items-center gap-1 text-[#0D9488] hover:underline font-medium cursor-pointer"
                >
                  <Copy size={14} />
                  Copy Citation
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
